/**
 * Planilha do módulo Legado servida pelo backend (lida do Google Drive).
 *
 * Por que existe: o celular e o tablet não conseguem escolher o .xlsm de 11 MB
 * que mora no computador do escritório. O backend lê o arquivo no Drive com a
 * conta de serviço da Pharu e o entrega autenticado, por unidade — o aparelho
 * só baixa quando a versão (md5) muda.
 *
 * `status`/`configuracao`/`salvarConfiguracao`/`testarConfiguracao` recebem o
 * `request` do AuthContext (refresh de token, erro com `payload.detalhe`). Todos
 * vão com `silentError`: o status roda a cada 30 s e as telas mostram o erro
 * inline ou pelo AsyncButton — o auto-toast duplicaria a mensagem.
 *
 * `baixarArquivo` usa fetch direto porque o `request` faz parse de texto/JSON e
 * aqui a resposta é binária (mesmo padrão de `baixarXlsLote`).
 */
import { ACCESS_API_URL } from "../config/api";
import { mensagemDeErro } from "../utils/errosApi";

const PREFIXO = "/api/planilha-legado";
const CHAVE_SESSAO = "pharuerp:access-session";
// 11 MB em rede móvel fraca passa fácil do timeout padrão de 45 s.
const TIMEOUT_DOWNLOAD_MS = 180000;
const NOME_PADRAO_ARQUIVO = "planilha-legado.xlsm";

function desembrulhar(resposta) {
  return resposta?.data ?? resposta;
}

export async function status(request) {
  return desembrulhar(await request(`${PREFIXO}/status`, { silentError: true }));
}

export async function configuracao(request) {
  return desembrulhar(await request(`${PREFIXO}/configuracao`, { silentError: true }));
}

export async function salvarConfiguracao(request, { driveLinkOuId, ativo, credencialJson = "" }) {
  const corpo = { drive_link_ou_id: driveLinkOuId, ativo: Boolean(ativo) };
  // A chave só vai quando a pessoa colou uma nova; vazio mantém a cadastrada.
  if (credencialJson && credencialJson.trim()) corpo.credencial_json = credencialJson.trim();
  return desembrulhar(
    await request(`${PREFIXO}/configuracao`, {
      method: "PUT",
      silentError: true,
      body: JSON.stringify(corpo),
    }),
  );
}

/** Data a partir da qual a Revisão vem antes da Aprovação (ISO ou null). */
export async function ordemExecutivo(request) {
  return desembrulhar(await request(`${PREFIXO}/ordem-executivo`, { silentError: true }));
}

export async function salvarOrdemExecutivo(request, revisaoAntesDesde) {
  return desembrulhar(
    await request(`${PREFIXO}/ordem-executivo`, {
      method: "PUT",
      body: JSON.stringify({ revisao_antes_desde: revisaoAntesDesde || null }),
      silentError: true,
    }),
  );
}

export async function testarConfiguracao(request) {
  return desembrulhar(
    await request(`${PREFIXO}/configuracao/testar`, { method: "POST", silentError: true }),
  );
}

// ---------------------------------------------------------------------------
// Histórico da planilha (Capacidade da Fábrica)
//
// A planilha recomeça todo janeiro, mas o ritmo da fábrica precisa de 26
// semanas: o backend guarda uma "foto" compacta de cada versão (as linhas que
// a capacidade usa + a projeção do dia) e os términos já vistos, que
// sobrevivem à virada do ano.
// ---------------------------------------------------------------------------

/**
 * `corpo` = `{origem: "drive"|"importacao", versao_arquivo, versao_leitor,
 * data_referencia, nome_arquivo, linhas, projecao}`. Idempotente pela versão:
 * `201 {id, criada:true, ...}` na primeira vez, `200 {id, criada:false}` depois.
 * 409 quando a versão do Drive não é mais a atual.
 */
export async function enviarFoto(request, corpo) {
  return desembrulhar(
    await request(`${PREFIXO}/fotos`, {
      method: "POST",
      silentError: true,
      body: JSON.stringify(corpo),
    }),
  );
}

/** `{fotos: [FOTO_META]}` da unidade, da mais recente para a mais antiga. */
export async function listarFotos(request) {
  return desembrulhar(await request(`${PREFIXO}/fotos`, { silentError: true }));
}

/** `{terminos: [TERMINO]}` com término a partir de `desde` ("AAAA-MM-DD"). */
export async function listarTerminos(request, { desde }) {
  const consulta = new URLSearchParams({ desde: String(desde) }).toString();
  return desembrulhar(await request(`${PREFIXO}/terminos?${consulta}`, { silentError: true }));
}

function lerAccessToken() {
  try {
    const sessao = JSON.parse(window.localStorage.getItem(CHAVE_SESSAO) || "{}");
    return sessao?.accessToken || null;
  } catch {
    return null;
  }
}

/** `"abc"` ou `W/"abc"` -> `abc`. */
function md5DoEtag(etag) {
  if (!etag) return null;
  return String(etag).replace(/^W\//, "").replace(/"/g, "").trim() || null;
}

/** Nome do arquivo no Content-Disposition (filename* UTF-8 tem prioridade). */
export function nomeDoContentDisposition(valor) {
  if (!valor) return null;
  const estendido = /filename\*\s*=\s*UTF-8''([^;]+)/i.exec(valor);
  if (estendido) {
    try {
      return decodeURIComponent(estendido[1].trim());
    } catch {
      /* cai para o filename simples */
    }
  }
  const simples = /filename\s*=\s*"?([^";]+)"?/i.exec(valor);
  return simples ? simples[1].trim() : null;
}

async function erroDaResposta(resposta) {
  let payload = null;
  try {
    payload = await resposta.json();
  } catch {
    payload = null;
  }
  const erro = new Error(mensagemDeErro(payload, resposta.status));
  erro.status = resposta.status;
  erro.payload = payload;
  return erro;
}

/**
 * Baixa o .xlsm da unidade.
 *
 * Com `md5Atual`, manda `If-None-Match` e o servidor responde 304 quando não
 * mudou: volta `{ naoMudou: true }` sem baixar nada. Senão volta
 * `{ naoMudou: false, buffer, md5, nome }`. `md5`/`nome` saem dos cabeçalhos
 * e podem vir `null` se o CORS não os expuser — quem chama completa com o
 * `status()`.
 *
 * Erro HTTP vira `Error` com `status` e a mensagem do `detalhe` do backend.
 */
export async function baixarArquivo(md5Atual) {
  const headers = {};
  const token = lerAccessToken();
  if (token) headers.Authorization = `Bearer ${token}`;
  if (md5Atual) headers["If-None-Match"] = `"${md5Atual}"`;

  const controle = typeof AbortController !== "undefined" ? new AbortController() : null;
  const timer = controle ? setTimeout(() => controle.abort(), TIMEOUT_DOWNLOAD_MS) : null;
  try {
    const resposta = await fetch(`${ACCESS_API_URL}${PREFIXO}/arquivo`, {
      headers,
      cache: "no-store",
      signal: controle?.signal,
    });
    if (resposta.status === 304) {
      return { naoMudou: true, md5: md5Atual };
    }
    if (!resposta.ok) {
      throw await erroDaResposta(resposta);
    }
    const buffer = await resposta.arrayBuffer();
    return {
      naoMudou: false,
      buffer,
      md5: md5DoEtag(resposta.headers.get("ETag")),
      nome:
        nomeDoContentDisposition(resposta.headers.get("Content-Disposition")) ||
        NOME_PADRAO_ARQUIVO,
    };
  } catch (erro) {
    if (erro?.name === "AbortError") {
      throw new Error("Tempo esgotado ao baixar a planilha do servidor.");
    }
    throw erro;
  } finally {
    if (timer) clearTimeout(timer);
  }
}

// IDs de arquivo do Drive: letras, números, "-" e "_" (hoje 33 ou 44 chars).
const RE_ID_DRIVE = /^[A-Za-z0-9_-]{20,}$/;
const RE_ID_NO_CAMINHO = /\/d\/([A-Za-z0-9_-]{20,})/;
const RE_ID_NA_QUERY = /[?&]id=([A-Za-z0-9_-]{20,})/;

/**
 * Link do Drive (qualquer formato comum) ou ID -> ID. Texto não reconhecido
 * volta aparado, sem mudança: quem decide se é válido é o backend (400).
 */
export function extrairIdArquivoDrive(texto) {
  const valor = String(texto || "").trim();
  if (!valor) return "";
  if (RE_ID_DRIVE.test(valor)) return valor;
  const noCaminho = RE_ID_NO_CAMINHO.exec(valor);
  if (noCaminho) return noCaminho[1];
  const naQuery = RE_ID_NA_QUERY.exec(valor);
  if (naQuery) return naQuery[1];
  return valor;
}
